import { App } from '@capacitor/app';
import { Tooltip, TooltipProps } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

export interface AppAdaptedTooltipProps extends TooltipProps {}

export const AppAdaptedTooltip = (props: AppAdaptedTooltipProps) => {
  const [open, setOpen] = useState(false);
  const suppressedRef = useRef(false);
  const suppressionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    const suppress = () => {
      suppressedRef.current = true;
      setOpen(false);
    };

    const scheduleUnsuppress = () => {
      if (suppressionTimerRef.current) {
        clearTimeout(suppressionTimerRef.current);
      }
      // Keep suppression active briefly to block ghost touch/OS events that
      // can re-open the tooltip immediately after the app foregrounds.
      suppressionTimerRef.current = setTimeout(() => {
        suppressedRef.current = false;
      }, 300);
    };

    // visibilitychange fires during the drag-to-recents gesture, well before
    // Capacitor's pause event, so it catches the "slow drag" stuck-tooltip case.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        suppress();
      } else {
        scheduleUnsuppress();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const pauseListener = App.addListener('pause', suppress);
    const resumeListener = App.addListener('resume', scheduleUnsuppress);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      pauseListener.then((handle) => handle.remove());
      resumeListener.then((handle) => handle.remove());

      if (suppressionTimerRef.current) {
        clearTimeout(suppressionTimerRef.current);
      }
    };
  }, []);

  return (
    <Tooltip
      open={open}
      {...props}
      onOpen={() => {
        if (!suppressedRef.current) {
          setOpen(true);
        }
      }}
      onClose={() => setOpen(false)}
    />
  );
};
